import { useState } from 'react';
import { useApi } from '../lib/useApi.ts';
import { useCanWrite } from '../lib/auth.tsx';
import { post } from '../lib/api.ts';
import { Badge, Button, Card, Skeleton } from '../components/ui/index.ts';
import { AlertIcon, CheckIcon, ClockIcon, SparkIcon } from '../components/icons.tsx';
import { cx } from '../lib/cx.ts';

type Verdict = 'ok' | 'warn' | 'fail';

interface Check {
  key: string;
  label: string;
  verdict: Verdict;
  detail: string;
  meta?: Record<string, unknown>;
}

interface Attempt {
  ip: string | null;
  outcome: string;
  role: string | null;
  attempted_at: string;
}

const ATTEMPT_TONE: Record<string, 'won' | 'risk' | 'lost'> = {
  success: 'won',
  invalid_password: 'risk',
  locked_out: 'lost',
};

interface Report {
  status: Verdict;
  checkedAt: string;
  checks: Check[];
}

const TONE: Record<Verdict, 'won' | 'risk' | 'lost'> = {
  ok: 'won',
  warn: 'risk',
  fail: 'lost',
};

const HEADLINE: Record<Verdict, string> = {
  ok: 'Everything is moving',
  warn: 'Something needs a look',
  fail: 'Something is broken',
};

/**
 * The answer to "is it quietly doing nothing".
 *
 * Process-up is not the question, so nothing here reports it. Each row is
 * about whether work is still moving through the system.
 */
export default function System() {
  const canWrite = useCanWrite();
  const { data, error, loading, reload } = useApi<Report>('/health/detail');
  const [probe, setProbe] = useState<{ ok: boolean; detail: string } | null>(null);
  const [probing, setProbing] = useState(false);

  async function runProbe() {
    setProbing(true);
    setProbe(null);
    try {
      const result = await post<{ ok: boolean; detail: string }>('/health/claude-probe', {});
      setProbe(result);
    } catch (err: unknown) {
      setProbe({ ok: false, detail: err instanceof Error ? err.message : 'Probe failed.' });
    } finally {
      setProbing(false);
      reload();
    }
  }

  if (loading) return <Skeleton className="h-64 w-full" />;
  if (error || !data) return <p className="text-sm text-lost">{error ?? 'Could not load health.'}</p>;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tighter">System</h1>
          <p className="mt-0.5 text-xs text-fg-subtle">
            Checked {new Date(data.checkedAt).toLocaleString()}
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={reload}>
          Re-check
        </Button>
      </header>

      <div
        className={cx(
          'flex items-center gap-3 rounded-lg border p-4',
          data.status === 'ok'
            ? 'border-won/30 bg-won-subtle'
            : data.status === 'warn'
              ? 'border-risk/30 bg-risk-subtle'
              : 'border-lost/30 bg-lost-subtle',
        )}
      >
        {data.status === 'ok' ? (
          <CheckIcon size={18} className="shrink-0 text-won" />
        ) : (
          <AlertIcon size={18} className="shrink-0 text-risk" />
        )}
        <div>
          <p className="text-sm font-medium text-fg">{HEADLINE[data.status]}</p>
          <p className="mt-0.5 text-xs text-fg-subtle">
            {data.checks.filter((c) => c.verdict !== 'ok').length === 0
              ? 'Every job has run recently and nothing is backed up.'
              : `${data.checks.filter((c) => c.verdict !== 'ok').length} of ${data.checks.length} checks need attention.`}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {data.checks.map((check) => (
          <Card key={check.key}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-fg">{check.label}</span>
                  <Badge tone={TONE[check.verdict]} dot>
                    {check.verdict}
                  </Badge>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-fg-muted">{check.detail}</p>
              </div>

              {check.key === 'claude_auth' && canWrite && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={probing}
                  leading={<SparkIcon size={13} />}
                  onClick={() => void runProbe()}
                >
                  Test now
                </Button>
              )}
            </div>

            {/* The login endpoint is the only thing on the public internet,
                so its recent history is worth seeing rather than summarising. */}
            {check.key === 'auth_attempts' && Array.isArray(check.meta?.['recent']) && (
              <div className="mt-3 border-t border-border-subtle pt-2.5">
                <p className="text-2xs text-fg-subtle">
                  Most recent attempts
                  {Number(check.meta?.['lockedOutNow'] ?? 0) > 0 && (
                    <span className="ml-2 text-lost">
                      {String(check.meta?.['lockedOutNow'])} address(es) locked out right now
                    </span>
                  )}
                </p>
                <ul className="mt-1.5 space-y-1">
                  {(check.meta['recent'] as Attempt[]).map((attempt, index) => (
                    <li key={index} className="flex items-center gap-2 text-xs">
                      <Badge tone={ATTEMPT_TONE[attempt.outcome] ?? 'neutral'}>
                        {attempt.outcome.replace(/_/g, ' ')}
                      </Badge>
                      <span className="numeric text-fg-muted">{attempt.ip ?? 'unknown'}</span>
                      {attempt.role && <span className="text-fg-subtle">as {attempt.role}</span>}
                      <span className="numeric ml-auto text-fg-subtle">
                        {new Date(attempt.attempted_at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
                {(check.meta['recent'] as Attempt[]).length === 0 && (
                  <p className="mt-1 text-xs text-fg-subtle">Nothing recorded yet.</p>
                )}
              </div>
            )}

            {check.key === 'claude_auth' && probe && (
              <p
                className={cx(
                  'mt-2.5 border-t border-border-subtle pt-2.5 text-xs',
                  probe.ok ? 'text-won' : 'text-lost',
                )}
              >
                {probe.detail}
              </p>
            )}
          </Card>
        ))}
      </div>

      <p className="flex items-start gap-1.5 text-2xs text-fg-subtle">
        <ClockIcon size={11} className="mt-px shrink-0" />
        The Claude test is manual on purpose. Each run costs about 28k tokens from the same
        subscription you work with, so it is never polled. Expiry is caught for free instead: if
        analysis work is queued and the job fails, that shows up above.
      </p>
    </div>
  );
}
